import { useMemo } from 'react'

import {
  digObject, generateID,
} from '@domain-group/javascript-utils'

import useMediaLinks from '@hooks/useMediaLinks'
import useOrderItems from '@hooks/useOrderItems'
import useSelector from '@hooks/useSelector'

import { useWatchEntityUpdates } from '@domain-group/react-hooks'

import {
  CampaignModel, MediaLinkModel, OrderItemModel,
} from '@models/types'

import { EntitiesState } from '@redux/entities'

const watchEntityKeys = ['mediaLinks', 'orderItems']

type CombineCampaignLinksParams = {
  filteredMediaLinks: MediaLinkModel[],
  filteredOrderItems: OrderItemModel[],
  interactiveImages: EntitiesState['interactiveImages'],
  microsites: EntitiesState['microsites'],
}

const combineCampaignLinks = (params: CombineCampaignLinksParams) => {
  const {
    filteredMediaLinks, filteredOrderItems, interactiveImages, microsites,
  } = params

  const links = [...filteredMediaLinks]

  filteredOrderItems.forEach((orderItem) => {
    const micrositeId = digObject(orderItem, 'microsite_id', null)
    const interactiveImageId = digObject(orderItem, 'interactive_image_id', null)

    const microsite = microsites[micrositeId] || {}
    const interactiveImage = interactiveImages[interactiveImageId] || {}

    const micrositeUrl = digObject(microsite, 'custom_domain', '')
    const interactiveImageUrl = digObject(interactiveImage, 'links.live_url', '')

    if (micrositeUrl){
      const link = {
        key: generateID(),
        link_type: 'microsite',
        url: `https://www.${micrositeUrl}`,
      }

      // @ts-expect-error TS2345
      links.push(link)
    }

    if (interactiveImageUrl){
      const link = {
        key: generateID(),
        link_type: 'interactive_image',
        url: interactiveImageUrl,
      }

      // @ts-expect-error TS2345
      links.push(link)
    }
  })

  return links
}

function useCampaignLinks(campaign: CampaignModel, options: { performHttpRequests: boolean }) {
  const { performHttpRequests } = options

  const { id } = campaign || {}

  const { interactiveImages, microsites } = useSelector(reduxState => reduxState.entities)

  const { filteredOrderItems } = useOrderItems({
    filters: {
      campaign_id: id,
    },
    performHttpRequests: performHttpRequests && !!id,
    requestOptions: {
      include_order_item_campaign_id: true,
      include_order_item_interactive_image: true,
      include_order_item_microsite: true,
    },
  })

  const { filteredMediaLinks } = useMediaLinks({
    filters: {
      linkable_type: 'Campaign',
      linkable_id: id,
    },
    performHttpRequests: performHttpRequests && !!id,
  })

  const {
    updatedEntities: { mediaLinks: mediaLinksUpdatedAt, orderItems: orderItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const campaignLinks = useMemo(() => {
    const filtered = combineCampaignLinks({
      filteredMediaLinks, filteredOrderItems, interactiveImages, microsites,
    })

    return filtered
  }, [mediaLinksUpdatedAt, orderItemsUpdatedAt])

  const hasCampaignLinks = !!campaignLinks.length

  return {
    campaignLinks,
    hasCampaignLinks,
  }
}

export default useCampaignLinks
